import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type TFunction = ReturnType<typeof useTranslation>["t"];

/** Error key for the "select at least one alert type" rule — a section-level
 * message with no input of its own. */
export const ALERT_TYPES_ERROR_PATH = "alert_types";

const isValidEmail = (value: string) => z.string().email().safeParse(value).success;

/**
 * Schema for the Add / Edit contact drawer. The drawer models a contact the
 * way the design does: a list of phone numbers and a list of email addresses,
 * each carrying the alert types it should serve. `contactId` is set when an
 * existing contact is being edited, empty when the drawer is creating the
 * person.
 */
const buildOccupantFormSchema = (t: TFunction) =>
  z
    .object({
      contactId: z.string(),
      first_name: z.string().trim(),
      last_name: z.string().trim(),
      phones: z.array(
        z.object({
          /** What this number is — "Home", "Work". Free text, optional. */
          label: z.string().trim(),
          value: z.string().trim(),
          alert_call: z.boolean(),
          alert_sms: z.boolean(),
        }),
      ),
      emails: z.array(
        z.object({
          label: z.string().trim(),
          value: z.string().trim(),
          alert_email: z.boolean(),
        }),
      ),
      is_primary: z.boolean(),
    })
    .superRefine((values, ctx) => {
      const filledPhones = values.phones.filter((phone) => phone.value !== "");
      const filledEmails = values.emails.filter((email) => email.value !== "");

      // An empty row is an error, however few there are — a box left blank was
      // meant to be filled, and the message belongs on that box. The single
      // exception is a lone blank row in one list while the other already
      // reaches the contact: an email-only occupant shouldn't be nagged for a
      // phone number it was never going to have.
      const isSparePlaceholder = (rowCount: number, otherFilled: number) => rowCount === 1 && otherFilled > 0;

      values.phones.forEach((phone, index) => {
        if (phone.value === "" && !isSparePlaceholder(values.phones.length, filledEmails.length)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phones", index, "value"],
            message: t("occupants:occupants-validation-phone", "Phone number is required"),
          });
        }
      });

      values.emails.forEach((email, index) => {
        if (email.value === "") {
          if (!isSparePlaceholder(values.emails.length, filledPhones.length)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["emails", index, "value"],
              message: t("occupants:occupants-validation-email-required", "Email is required"),
            });
          }

          return;
        }

        if (!isValidEmail(email.value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["emails", index, "value"],
            message: t("occupants:occupants-validation-email", "Enter a valid email address"),
          });
        }
      });

      // An existing contact is edited in place; a new one still needs a name.
      if (values.contactId === "" && values.first_name === "" && values.last_name === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["first_name"],
          message: t("occupants:occupants-validation-first-name", "First name is required"),
        });
      }

      // Every blank row has just been flagged on its own input; repeating it on
      // the form would only say the same thing twice.
      if (filledPhones.length === 0 && filledEmails.length === 0) {
        return;
      }

      const hasAlert =
        filledPhones.some((phone) => phone.alert_call || phone.alert_sms) ||
        filledEmails.some((email) => email.alert_email);

      // Belongs to no single input — reported on the form and rendered under
      // the destination lists. Deliberately NOT `root`: react-hook-form clears
      // `errors.root` inside handleSubmit before deciding whether the form is
      // valid, so a lone root issue submits an empty value object.
      if (!hasAlert) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [ALERT_TYPES_ERROR_PATH],
          message: t("occupants:occupants-validation-alert-type", "Select at least one alert type"),
        });
      }
    });

export const useOccupantFormSchema = () => {
  const { t } = useTranslation(["occupants"]);

  return useMemo(() => buildOccupantFormSchema(t), [t]);
};

export type OccupantFormValues = z.infer<ReturnType<typeof buildOccupantFormSchema>>;

/** One empty row of each, so the form opens with something to type into. */
export const OCCUPANT_FORM_DEFAULTS: OccupantFormValues = {
  contactId: "",
  first_name: "",
  last_name: "",
  phones: [{ label: "", value: "", alert_call: false, alert_sms: false }],
  emails: [{ label: "", value: "", alert_email: false }],
  is_primary: false,
};
